import { useState } from "react";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

type DeleteActions = {
  onDeactivateRecipe: () => Promise<{ ok: true } | { ok: false; error: string }>;
  onPermanentlyDeleteRecipe: (
    id: string,
  ) => Promise<
    | { ok: true }
    | { ok: false; error: string; blockers?: Array<{ code: string; message: string }> }
  >;
};

/** Deactivate / permanent-delete confirm state for Models panel modals. */
export function useManufacturingModelsPanelDeletes(actions: DeleteActions) {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [permanentDeleteOpen, setPermanentDeleteOpen] = useState(false);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState<MfgRecipeSummary | null>(
    null,
  );
  const [permanentDeleteBlockers, setPermanentDeleteBlockers] = useState<
    Array<{ code: string; message: string }>
  >([]);

  async function confirmDeactivateModel() {
    setDeleteBusy(true);
    setDeleteError(null);
    const r = await actions.onDeactivateRecipe();
    setDeleteBusy(false);
    if (!r.ok) {
      setDeleteError(r.error);
      return;
    }
    setDeleteConfirmOpen(false);
    setDeleteError(null);
  }

  function openPermanentDelete(recipe: MfgRecipeSummary) {
    setPermanentDeleteTarget(recipe);
    setPermanentDeleteBlockers([]);
    setDeleteError(null);
    setPermanentDeleteOpen(true);
  }

  async function confirmPermanentDelete() {
    if (!permanentDeleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    setPermanentDeleteBlockers([]);
    const r = await actions.onPermanentlyDeleteRecipe(permanentDeleteTarget.id);
    setDeleteBusy(false);
    if (!r.ok) {
      setDeleteError(r.error);
      setPermanentDeleteBlockers(r.blockers ?? []);
      return;
    }
    setPermanentDeleteOpen(false);
    setPermanentDeleteTarget(null);
    setDeleteError(null);
    setPermanentDeleteBlockers([]);
  }

  return {
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    deleteBusy,
    deleteError,
    setDeleteError,
    permanentDeleteOpen,
    permanentDeleteTarget,
    permanentDeleteBlockers,
    confirmDeactivateModel,
    openPermanentDelete,
    confirmPermanentDelete,
    cancelDeactivate: () => {
      if (deleteBusy) return;
      setDeleteConfirmOpen(false);
      setDeleteError(null);
    },
    cancelPermanentDelete: () => {
      if (deleteBusy) return;
      setPermanentDeleteOpen(false);
      setPermanentDeleteTarget(null);
      setDeleteError(null);
      setPermanentDeleteBlockers([]);
    },
  };
}
